/**
 * Menüwahl NACH der Buchung (Gastseite, sql/772, docs/KONZEPT-PAKET-MENUEWAHL.md Ablauf 4): der Betrieb hat „Menüauswahl an
 * Gast senden“ gedrückt, der Gast kommt über den Link der Mail auf seine Angebotsseite und verteilt die Gäste auf die Menüs.
 * Dieselben Karten mit Zählern wie vor der Annahme (`MenuewahlGastAnsicht`); „Menüauswahl bestätigen“ erst bei vollständiger
 * Summe. Der Betrag kommt ausschließlich vom Server: ein Mehr- oder Minderbetrag steht in der Antwort, der Browser rechnet nie.
 */
import { useMemo, useState } from "react";
import { AT, MenuewahlGastAnsicht, menueDatum } from "./AngebotsAuswahl";
import { money } from "./format";
import { Button, Icon } from "./components";
import { menuewahlenAus, menuewahlLage, type AngebotOption, type Mengen } from "./angebot-auswahl-modell";
import type { PublicOffer, PublicOfferItem } from "./types";

type Lage = NonNullable<PublicOffer["event"]["menuewahl"]>;

interface Antwort {
  data?: { ok?: boolean; bereits?: boolean; revision?: number; amount_total_cents?: number; differenz_cents?: number; faellig_cents?: number | null; ausgleich_offen?: boolean };
  message?: string;
  error?: string;
}

export function MenuewahlGastNachBuchung({
  lang,
  markenfarbe,
  token,
  vorschau,
  option,
  lage,
  onGespeichert,
}: {
  lang: "de" | "en";
  markenfarbe: string | null | undefined;
  /** Gast-Token; in der Vorschau des Betriebs null (dann wird nichts gespeichert). */
  token: string | null;
  vorschau: boolean;
  option: AngebotOption;
  lage: Lage;
  /** Nach dem Speichern: Seite neu laden (neue Revision, gespeicherte Wahl). */
  onGespeichert: () => void;
}) {
  const t = AT[lang];
  const wahlen = useMemo(() => menuewahlenAus(option), [option]);
  const [mengen, setMengen] = useState<Mengen>(() => {
    const start: Mengen = {};
    for (const m of wahlen) start[m.gruppeId] = { ...(option.chosen_alternatives?.[m.gruppeId] ?? {}) };
    return start;
  });
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<NonNullable<Antwort["data"]> | null>(null);

  const name = (it: PublicOfferItem) => it.translations?.[lang]?.name ?? it.name;
  const beschreibung = (it: PublicOfferItem) => {
    const x = it.translations?.[lang]?.description ?? it.description;
    return x && x.trim() ? x : null;
  };
  const soll = wahlen.reduce((s, m) => Math.max(s, m.soll), 0);
  const mitAufpreis = wahlen.some((m) => m.menues.some((x) => x.aufpreisCents > 0));
  const frist = menueDatum(lage.frist, lang);
  const alleOk = wahlen.length > 0 && wahlen.every((m) => menuewahlLage(m, mengen).ok);
  const offen = lage.offen === true;
  const rest = wahlen.reduce((s, m) => s + menuewahlLage(m, mengen).offen, 0);
  const zuViel = wahlen.reduce((s, m) => s + menuewahlLage(m, mengen).zuViel, 0);

  function setzeMenge(gruppeId: string, itemId: string, n: number) {
    setMengen((m) => ({ ...m, [gruppeId]: { ...(m[gruppeId] ?? {}), [itemId]: n } }));
    setFehler(null);
    setErgebnis(null);
  }

  async function bestaetigen() {
    if (!alleOk || busy) return;
    if (vorschau || !token) {
      // Vorschau des Betriebs: nichts wird gespeichert.
      setErgebnis({ ok: true });
      return;
    }
    setBusy(true);
    setFehler(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/menuewahl`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ wahl: mengen, revision: lage.revision }),
      });
      const body = (await res.json().catch(() => ({}))) as Antwort;
      if (!res.ok || !body.data) {
        setFehler(body.message ?? t.menueSpeichertFehler);
        return;
      }
      setErgebnis(body.data);
      onGespeichert();
    } catch {
      setFehler(t.menueSpeichertFehler);
    } finally {
      setBusy(false);
    }
  }

  const diff = ergebnis?.differenz_cents ?? 0;
  return (
    <section className="mt-5 max-w-[960px] text-left" data-testid="menuewahl-nach-buchung">
      <div className="rounded-[14px] border border-outline bg-[#fffdf8] px-[18px] py-5 dark:border-outline/30 dark:bg-inverse-surface">
        <h2 className="font-serif text-[25px] font-medium leading-[1.2] text-on-surface dark:text-inverse-on-surface">{t.menueGebuchtTitel}</h2>
        <p className="mt-1.5 text-[15px] leading-normal text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="menuewahl-intro">
          {mitAufpreis ? t.menueGebuchtTextAufpreis(soll, frist) : t.menueGebuchtText(soll, frist)}
        </p>
        {offen ? (
          <>
            <div className="mt-2">
              <MenuewahlGastAnsicht lang={lang} markenfarbe={markenfarbe} option={option} mengen={mengen} onMenge={setzeMenge} aktiv={!busy} name={name} beschreibung={beschreibung} />
            </div>
            <div className="ga-aktion mt-4 flex flex-wrap items-center justify-between gap-3">
              <span className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" aria-live="polite" data-testid="menuewahl-rest">
                {zuViel > 0 ? t.menueZuViel(zuViel) : alleOk ? t.menueFertig(soll) : t.menueOffen(rest)}
              </span>
              <Button type="button" variant="primary" disabled={!alleOk || busy} onClick={() => void bestaetigen()} data-testid="menuewahl-bestaetigen">
                {busy ? t.menueSpeichert : t.menueBestaetigen}
              </Button>
            </div>
            {fehler && (
              <p role="alert" className="mt-3 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="menuewahl-fehler">
                {fehler}
              </p>
            )}
            {ergebnis && (
              <p className="mt-3 flex items-start gap-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface" data-testid="menuewahl-danke">
                <Icon name="check_circle" className="mt-0.5 shrink-0 text-[18px] text-tertiary" />
                <span>
                  {t.menueDanke}
                  {diff > 0 ? ` ${t.menueMehrbetrag(money(diff))}` : diff < 0 ? ` ${t.menueMinderbetrag(money(-diff))}` : ""}
                </span>
              </p>
            )}
          </>
        ) : (
          <p className="mt-3 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="menuewahl-abgelaufen">
            {t.menueFristAbgelaufen}
          </p>
        )}
      </div>
    </section>
  );
}
